/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 *
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(module.exports, {
    namespace: "email.image",
    title: "Email Image",
    description: "A responsive, centered image row for platform emails. Optionally links out. Absolute URLs only.",
    supportServer: true,
    control: false,
    foruseIn: ["email"],
    fields: {
        "src": {
            title: "Image URL",
            type: "uri",
            description: "The absolute URL of the image to display. Must be publicly reachable (email clients cannot load relative paths).",
            required: true
        },
        "alt": {
            title: "Alt Text",
            type: "string",
            description: "Alternative text shown when the image cannot load / for screen readers.",
            required: false,
            default: ""
        },
        "width": {
            title: "Width",
            type: "number",
            description: "The intended display width in pixels (capped to the 480px email column; images stay fluid up to this).",
            required: false
        },
        "href": {
            title: "Link URL",
            type: "uri",
            description: "Optional absolute URL to wrap the image in a link.",
            required: false
        },
        "padding": {
            title: "Padding",
            type: "string",
            description: "CSS padding for the image row (1-4 space-separated lengths). Default '12px 20px'. Set '0px' for a full-bleed hero that runs to the column edge.",
            required: false
        }
    }
});

var $ = require("../../../uam/scafolding.js");
var urls = require("../email/urls.js");

// Escape a value for safe insertion into a double-quoted HTML attribute so a
// user-supplied alt / URL containing a quote cannot break out of the attribute.
function escAttr(s) {
    return String(s == null ? "" : s)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

// Sanitize a CSS padding string to 1-4 space-separated lengths (px/rem/em/%). A
// malformed value falls back to the default, so a template can never inject CSS.
// Same sanitizer email.card and email.section already use for their own padding
// field -- this element was the odd one out with a hardcoded gutter.
function resolvePadding(value, fallback) {
    if (typeof value === "string") {
        var v = value.trim();
        if (/^(\d+(\.\d+)?(px|rem|em|%)\s*){1,4}$/.test(v + " ")) { return v; }
    }
    return fallback;
}

// Resolve a possibly-relative asset path to an ABSOLUTE URL (email clients cannot
// render relative paths). If already absolute (http/https/protocol-relative or a
// data: URI) it is returned untouched; otherwise it is joined to the PUBLIC email
// origin (uat/src/email/urls.js) — never config.my.app.url, which is the ENV host
// and would ship a dev URL (…​.test) into a recipient's inbox.
function toAbsolute(src) {
    var s = (typeof src === "string") ? src.trim() : "";
    if (!s) { return ""; }
    if (/^(https?:)?\/\//i.test(s) || /^data:/i.test(s)) { return s; }
    var joined = urls.emailUrl($.config || {}, s);
    return joined || s;
}

exports.render = async function (options) {
    var { html } = await exports.renderEmail(options);
    return [{
        n: "ua.element.serverNotSupported",
        inner: {
            n: "json",
            type: "ua/interface",
            by: "email.image",
            i: JSON.stringify({
                n: "html",
                html: `<div style="max-width:480px;margin:0 auto;font-family:Arial,Helvetica,sans-serif;">${html}</div>`
            })
        }
    }];
};

exports.renderEmail = async function (options) {
    options = options || {};
    var src   = toAbsolute(options.src);
    var alt   = (typeof options.alt === "string") ? options.alt : "";
    var href  = (options.href) ? toAbsolute(options.href) : "";

    // Width caps to the 480px column; the image stays fluid below that.
    var widthNum = parseInt(options.width, 10);
    if (!(widthNum > 0) || widthNum > 480) { widthNum = 480; }
    // Paper theme + bleed: the image runs the full sheet width (the shell skips the
    // side padding for bleed rows), so it caps at the sheet width instead.
    var T = require("../email/Style.js").emailTheme($.config || {});
    var bleed = !!(T && options.bleed === true);
    if (bleed) { widthNum = T.width; }

    var imgStyle = "display:block;width:100%;max-width:" + widthNum + "px;height:auto;border:0;outline:none;text-decoration:none;margin:0 auto;";
    var imgHtml = `<img src="${escAttr(src)}" alt="${escAttr(alt)}" width="${widthNum}" style="${imgStyle}" />`;

    var inner = href
        ? `<a href="${escAttr(href)}" style="text-decoration:none;border:0;display:block;">${imgHtml}</a>`
        : imgHtml;

    // The gutter is a DEFAULT, not a law. It used to be hardcoded, which made a
    // full-bleed hero impossible from a template: a card could zero its own
    // padding and the image row still inset itself 20px, so the hero always read
    // as narrower than the cards above and below it.
    var padding = resolvePadding(options.padding, bleed ? "0" : "12px 20px");

    var html = `<tr>
  <td align="center" style="padding:${padding};">
    ${inner}
  </td>
</tr>`;

    var text = href
        ? (alt ? `${alt}: ${href}` : href)
        : (alt || src);

    return { html, text };
};
